import React from "react";
import { FormikProps } from "formik";
import ATMLoadingButton from "src/components/UI/atoms/ATMLoadingButton/ATMLoadingButton";
import ATMTextField from "src/components/UI/atoms/formFields/ATMTextField/ATMTextField";
import { BatchFormValues } from "src/models/Batch.model";
import useCourse from "src/hooks/useCourse";
import ATMSelect from "src/components/UI/atoms/formFields/ATMSelect/ATMSelect";
import useModes from "src/hooks/useModes";
import ATMCheckbox from "src/components/UI/atoms/formFields/ATMCheckbox/ATMCheckbox";
import ATMDatePicker from "src/components/UI/atoms/formFields/ATMDatePicker/ATMDatePicker";
import useBranches from "src/hooks/useGetBranches";
import useLanguages from "src/hooks/useLanguages";
import useTrainers from "src/hooks/useTrainers";
import ATMMultiSelect from "src/components/UI/atoms/formFields/ATMMultiSelect/ATMMultiSelect";
import { handleValidNumber } from "src/utils/validations/numberInput";
import { Backdrop, CircularProgress } from "@mui/material";
import { useGetAllPlansQuery } from "src/services/CourseServices";
import moment from "moment";

type Props = {
  formikProps: FormikProps<BatchFormValues>;
  onClose: () => void;
  formType?: "ADD" | "EDIT";
  isLoading?: boolean;
};

const WeeokOffsOptions = [
  {
    label: "Monday",
    value: "Monday",
  },
  {
    label: "Tuesday",
    value: "Tuesday",
  },
  {
    label: "Wednesday",
    value: "Wednesday",
  },
  {
    label: "Thursday",
    value: "Thursday",
  },
  {
    label: "Friday",
    value: "Friday",
  },
  {
    label: "Saturday",
    value: "Saturday",
  },
  {
    label: "Sunday",
    value: "Sunday",
  },
];

const BatchForm = ({ formikProps, onClose, formType, isLoading }: Props) => {
  const { values, setFieldValue, isSubmitting, handleBlur } = formikProps;

  const { course, isDataLoading: isCourseLoading } = useCourse();
  const { modes, isDataLoading: isModeLoading } = useModes();
  const { branches, isDataLoading: isBranchesLoading } = useBranches();
  const { languages, isDataLoading: isLanguagesLoading } = useLanguages();
  const { trainers, isDataLoading: isTrainersLoading } = useTrainers();

  const {
    data: allPlans,
    isLoading: allPlansLoading,
    isFetching: allPlansFetching,
  } = useGetAllPlansQuery(
    {
      limit: 10,
      searchValue: "",
      params: ["planName", "noOfDays", "courseId"],
      page: 1,
      filterBy: [{ fieldName: "courseId", value: values?.course?.value }],
      dateFilter: {},
      orderBy: "createdAt",
      orderByValue: -1,
      isPaginationRequired: false,
    },
    { skip: !values?.course }
  );

  const PlansOptions = allPlans?.data?.map((el: any) => {
    return {
      label: el?.planName,
      value: el?._id,
    };
  });

  if (
    isLoading ||
    isCourseLoading ||
    isModeLoading ||
    isBranchesLoading ||
    isLanguagesLoading ||
    isTrainersLoading
  ) {
    return (
      <Backdrop open>
        {" "}
        <CircularProgress size={30} />{" "}
      </Backdrop>
    );
  }

  return (
    <div className="p-4 flex flex-col gap-6">
      <div className="flex justify-between items-center">
        <div className="text-xl font-medium">
          {" "}
          {formType === "EDIT" ? "Update Batch" : "Add Batch"}{" "}
        </div>
        <button type="button" className="hover:bg-hover" onClick={onClose}>
          Cancel
        </button>
      </div>
      <div className="grid grid-cols-12 gap-4">
        {/* Batch Name */}
        <div className="col-span-full">
          <ATMTextField
            required
            name="batchName"
            value={values.batchName}
            onChange={(e) => setFieldValue("batchName", e.target.value)}
            label="Batch Name"
            placeholder="Enter Batch Name"
            onBlur={handleBlur}
          />
        </div>

        {/* Course */}
        <div className="col-span-6">
          <ATMSelect
            required
            name="course"
            value={values.course}
            onChange={(newValue) => {
              setFieldValue("course", newValue);
              setFieldValue("plan", "");
            }}
            onBlur={handleBlur}
            label="Course"
            placeholder="Select Course"
            options={course?.map((c) => ({
              label: c.courseName,
              value: c._id,
            }))}
            isLoading={isCourseLoading}
          />
        </div>
        {/* Plan */}
        <div className="col-span-6">
          <ATMSelect
            name="plan"
            required
            value={values?.plan}
            onChange={(newValue) => setFieldValue("plan", newValue)}
            onBlur={handleBlur}
            label="Plan"
            placeholder="Select Plan"
            options={PlansOptions}
            isLoading={allPlansLoading || allPlansFetching}
          />
        </div>
        <div className="col-span-6">
          <ATMMultiSelect
            name="weekOffs"
            value={values?.weekOffs}
            onChange={(newValue) => setFieldValue("weekOffs", newValue)}
            onBlur={handleBlur}
            label="Week Offs"
            placeholder="Select Week Offs"
            options={WeeokOffsOptions}
          />
        </div>

        {/* Mode */}
        <div className="col-span-6">
          <ATMSelect
            required
            name="mode"
            value={values.mode}
            onChange={(newValue) => setFieldValue("mode", newValue)}
            onBlur={handleBlur}
            label="Mode"
            placeholder="Select Mode"
            options={modes}
            isLoading={isModeLoading}
            renderOption={(option, { context }) => {
              if (context === "menu") {
                return <div className="capitalize">{option?.modeName}</div>;
              } else {
                return <div className="capitalize">{option?.modeName}</div>;
              }
            }}
            isOptionEqualToValue={(option, selected) => {
              return option?._id === selected?._id;
            }}
            isSearchedOption={(option, searchValue) => {
              return searchValue
                ? option?.data?.modeName?.toLowerCase()?.includes(searchValue)
                : true;
            }}
          />
        </div>

        {/* For All Branches */}
        <div className="mt-1 col-span-full">
          <ATMCheckbox
            checked={values.allBranches}
            onChange={(newValue) => {
              setFieldValue("allBranches", newValue);
              newValue
                ? setFieldValue(
                    "branches",
                    branches?.map((branch: any) => ({
                      label: `${branch?.branchAddress}, ${
                        branch?.city || ""
                      }, ${branch?.country || ""}`,
                      value: branch?._id,
                    }))
                  )
                : setFieldValue("branches", []);
            }}
            label="All Branches"
            disabled={isBranchesLoading}
          />
        </div>

        {/* Branches */}
        {values?.allBranches ? null : (
          <div className="col-span-full">
            <ATMMultiSelect
              name="branches"
              value={values.branches}
              onChange={(newValue) => setFieldValue("branches", newValue)}
              onBlur={handleBlur}
              label="Branches"
              placeholder="Select Branches"
              options={branches?.map((branch: any) => ({
                label: branch?.branchName,
                value: branch?._id,
              }))}
              isLoading={isBranchesLoading}
            />
          </div>
        )}

        {/* Start Date */}
        <div className="col-span-4">
          <ATMDatePicker
            required
            name="startDate"
            value={values.startDate || null}
            onChange={(newValue) => setFieldValue("startDate", newValue)}
            label="Select Date"
          />
        </div>

        {/* Start Time */}
        <div className="col-span-4">
          <ATMTextField
            type="time"
            name="startTime"
            value={moment(values?.startTime, "HH:mm").format("HH:mm")}
            onChange={(e) => {
              let time = moment(e.target.value, "HH:mm");
              setFieldValue("startTime", time);
            }}
            label="Start Time"
            onBlur={handleBlur}
            required
            placeholder="Please Start Time"
          />
        </div>

        {/* End Time */}
        <div className="col-span-4">
          <ATMTextField
            type="time"
            name="endTime"
            value={moment(values?.endTime, "HH:mm").format("HH:mm")}
            onChange={(e) => {
              let time = moment(e.target.value, "HH:mm");
              setFieldValue("endTime", time);
            }}
            label="End Time"
            onBlur={handleBlur}
            required
            placeholder="Please End Time"
          />
        </div>

        {/* Trainers */}
        <div className="col-span-full">
          <ATMMultiSelect
            name="trainers"
            value={values.trainers}
            onChange={(newValue) => setFieldValue("trainers", newValue)}
            onBlur={handleBlur}
            label="Trainers"
            placeholder="Select Trainers"
            options={trainers?.map((trainer: any) => ({
              label: `${trainer?.name}`,
              value: trainer?._id,
            }))}
            isLoading={isTrainersLoading}
          />
        </div>

        {/* Language */}
        <div className="col-span-4">
          <ATMSelect
            name="languages"
            value={values.language}
            onChange={(newValue) => setFieldValue("language", newValue)}
            onBlur={handleBlur}
            label="Language"
            placeholder="Select Language"
            options={languages}
            isLoading={isLanguagesLoading}
            renderOption={(option, { context }) => {
              if (context === "menu") {
                return <div className="capitalize">{option?.languageName}</div>;
              } else {
                return <div className="capitalize">{option?.languageName}</div>;
              }
            }}
            isOptionEqualToValue={(option, selected) => {
              return option?._id === selected?._id;
            }}
            isSearchedOption={(option, searchValue) => {
              return searchValue
                ? option?.data?.languageName
                    ?.toLowerCase()
                    ?.includes(searchValue)
                : true;
            }}
          />
        </div>

        {/* Fees */}
        <div className="col-span-4">
          <ATMTextField
            required
            name="fees"
            value={values.fees}
            onChange={(e) => {
              handleValidNumber(e) && setFieldValue("fees", e.target.value);
            }}
            label="Fees"
            placeholder="Enter Fees"
            onBlur={handleBlur}
          />
        </div>

        {/* No. of seats */}
        <div className="col-span-4">
          <ATMTextField
            required
            name="seats"
            value={values.seats}
            onChange={(e) => {
              handleValidNumber(e) && setFieldValue("seats", e.target.value);
            }}
            label="No. of seats"
            placeholder="Enter No. of seats"
            onBlur={handleBlur}
          />
        </div>
      </div>
      <div>
        <ATMLoadingButton isLoading={isSubmitting} type="submit">
          Save
        </ATMLoadingButton>
      </div>
    </div>
  );
};

export default BatchForm;
